import { createContext, useState, useContext, useEffect } from "react"
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth"
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../services/firebase"

const AuthContext = createContext()

// Safe fallback when email is null (e.g. signInWithCustomToken)
const nameFromEmail = (displayName, email) =>
  displayName || (email ? email.split("@")[0] : "")

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const auth = getAuth()

  const refreshAuth = async () => {
    const currentUser = auth.currentUser
    if (currentUser) {
      try {
        await currentUser.getIdToken(true)

        const userDoc = await getDoc(doc(db, "users", currentUser.uid))

        let userData = {
          uid: currentUser.uid,
          email: currentUser.email,
          name: nameFromEmail(currentUser.displayName, currentUser.email),
          authenticated: true,
        }

        if (userDoc.exists()) {
          userData = { ...userData, ...userDoc.data() }
        }

        setUser(userData)
        console.log("Auth state manually refreshed for:", currentUser.email)
      } catch (error) {
        console.error("Error refreshing auth state:", error)
      }
    } else {
      setUser({ authenticated: false })
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        console.log("User authenticated:", firebaseUser.email)
        try {
          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid))

          let userData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: nameFromEmail(firebaseUser.displayName, firebaseUser.email),
            authenticated: true,
          }

          if (userDoc.exists()) {
            userData = { ...userData, ...userDoc.data() }

            await setDoc(
              doc(db, "users", firebaseUser.uid),
              { lastLogin: serverTimestamp() },
              { merge: true },
            )
          } else {
            // Only create the doc if this isn't a temporary custom-token session
            // (custom token users have no email — the real doc gets created in RegisterPage)
            if (firebaseUser.email) {
              await setDoc(doc(db, "users", firebaseUser.uid), {
                email: firebaseUser.email,
                name: nameFromEmail(
                  firebaseUser.displayName,
                  firebaseUser.email,
                ),
                createdAt: serverTimestamp(),
                lastLogin: serverTimestamp(),
              })
            }
          }

          setUser(userData)
        } catch (error) {
          console.error("Error fetching user data:", error)
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: nameFromEmail(firebaseUser.displayName, firebaseUser.email),
            authenticated: true,
          })
        }
      } else {
        setUser({ authenticated: false })
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [auth])

  const logout = async () => {
    // Fire-and-forget — don't await so a slow/offline Firestore never blocks logout
    if (user?.uid) {
      setDoc(doc(db, "users", user.uid), { lastLogout: serverTimestamp() }, { merge: true })
        .catch((e) => console.warn("Could not update logout timestamp:", e))
    }
    try {
      await signOut(auth)
      return true
    } catch (error) {
      console.error("Error signing out:", error)
      return false
    } finally {
      setUser({ authenticated: false })
    }
  }

  const updateUser = async (userData) => {
    try {
      if (!userData.uid && userData.hasOwnProperty("authenticated")) {
        setUser(userData)
        return
      }
      if (user?.uid) {
        await setDoc(
          doc(db, "users", user.uid),
          { ...userData, updatedAt: serverTimestamp() },
          { merge: true },
        )
        setUser({ ...user, ...userData })
      }
    } catch (error) {
      console.error("Error updating user data:", error)
      throw error
    }
  }

  return (
    <AuthContext.Provider
      value={{ user, loading, updateUser, logout, refreshAuth }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
export default AuthContext